'use client'

import {useEffect, useRef, useState} from 'react'
import Image from 'next/image'
import PixelCharacter from './PixelCharacter'
import { motion, AnimatePresence } from 'framer-motion'
import {Target, Users, Loader2, AlertCircle, User} from 'lucide-react'
import LoveRunGame from './LoveRunGame'
import type { AutocompleteResult } from '@/services/api'
import FloatingEmoji from '../ui/FloatingEmoji'
import {
	BrideIcon,
	GroomIcon,
	MagicStarIcon,
} from './LoveRunGame/utils/CustomIcons'
import JeuJulia from './JeuJulia'
import JeuBen from './JeuBen'
import {apiService} from "@/services/api";
import AttemptManager from './AttemptManager'

// ---- Types ----
type PastaChoice = 'pates' | 'patate'
type CheeseChoice = 'feta' | 'mozzarella'
type WaterChoice = 'cristaline' | 'montroucou'
type PlayerTeam = 'julia' | 'benjamin'

interface DuelQuote {
	id: number
	text: string
	author: PlayerTeam
}

interface GameResult {
	questionId: number
	userAnswer: PlayerTeam
	correctAnswer: PlayerTeam
	isCorrect: boolean
}

const duelQuotes: DuelQuote[] = [
	{ id: 1, text: "Qui a pleuré lors de la défaite du PSG en final de la ligue des Champions en 2020 ?", author: 'julia' },
	{ id: 2, text: "Je déteste la Coriandre.", author: 'benjamin' },
	{ id: 3, text: "Une pharmacie est pour moi aussi magique que la caverne d’Ali Baba !", author: 'benjamin' },
	{ id: 4, text: "Les cavistes du Bon Marché m’appellent par mon prénom.", author: 'julia' },
	{ id: 5, text: "J’ai eu la chance de manipuler les All Blacks (les vrais) en 2013.", author: 'julia' },
	{ id: 6, text: "Je peux réciter tous les pays du Monde (196) en moins de 5 minutes.", author: 'benjamin' },
	{ id: 7, text: "Si ce n’est pas brûlé, c’est immangeable.", author: 'julia' },
	{ id: 8, text: "Un concombre ? Le légume le plus inutile !", author: 'benjamin' },
	{ id: 9, text: "En un an j'ai reçu 34 PV de stationnement.", author: 'julia' },
	{ id: 10, text: "J'ai officiellement plus d'accidents que doudou", author: 'benjamin' }
]

const EnAttendantLeJourJ = () => {
	const [currentStep, setCurrentStep] = useState<number>(1)
	const [playerName, setPlayerName] = useState<string>('')
	const [playerNameConfirmed, setPlayerNameConfirmed] = useState(false)
	const [teamPasta, setTeamPasta] = useState<PastaChoice | null>(null)
	const [teamCheese, setTeamCheese] = useState<CheeseChoice | null>(null)
	const [teamWater, setTeamWater] = useState<WaterChoice | null>(null)
	const [playerTeam, setPlayerTeam] = useState<PlayerTeam | null>(null)
	const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(0)
	const [gameResults, setGameResults] = useState<GameResult[]>([])
	const [showResult, setShowResult] = useState(false)
	const [gameCompleted, setGameCompleted] = useState(false)
	const [resultSent, setResultSent] = useState(false)
	const [score, setScore] = useState(0)
	const [miniGameScore, setMiniGameScore] = useState<number | null>(null)

	// --- AUTOCOMPLETE: state
	const [suggestions, setSuggestions] = useState<AutocompleteResult[]>([])
	const [showSuggestions, setShowSuggestions] = useState(false)
	const [isLoadingName, setIsLoadingName] = useState(false)
	const [activeIdx, setActiveIdx] = useState<number>(-1)
	const nameInputRef = useRef<HTMLInputElement>(null)
	const suggestionsRef = useRef<HTMLDivElement>(null)
	const [selectedGuestId, setSelectedGuestId] = useState<number | null>(null)

	const prepareGameScores = (loveRunScore?: number) => {
		return {
			"quiz-general": { score, maxScore: 10 },
			"mini-jeu-ben": {
				score: playerTeam === 'benjamin' && miniGameScore !== null ? miniGameScore : 0,
				maxScore: 2
			},
			"mini-jeu-julia": {
				score: playerTeam === 'julia' && miniGameScore !== null ? miniGameScore : 0,
				maxScore: 2
			},
			"loverungame": { score: loveRunScore || 0, maxScore: 500 }
		}
	}

	// --- AUTOCOMPLETE: fetch suggestions (à l’entrée utilisateur)
	const handlePlayerInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value
		setPlayerName(value)
		setPlayerNameConfirmed(false)
		setShowSuggestions(true)
		setActiveIdx(-1)

		if (value.trim().length < 2) {
			setSuggestions([])
			setIsLoadingName(false)
			return
		}

		setIsLoadingName(true)
		try {
			const res = await apiService.searchGuests(value)
			// compat API (array direct ou {data: [...]})
			const list = Array.isArray(res) ? res : (res?.data ?? [])
			setSuggestions(Array.isArray(list) ? list.slice(0, 8) : [])
		} catch {
			setSuggestions([])
		} finally {
			setIsLoadingName(false)
		}
	}

// --- AUTOCOMPLETE: sélection d’une suggestion
// 2. Modifiez selectGuest pour stocker l'ID réel
	const selectGuest = (g: AutocompleteResult) => {
		setPlayerName(`${g.firstName} ${g.lastName}`)
		setSelectedGuestId(g.id) // 👈 Stockez l'ID réel !
		setShowSuggestions(false)
		setActiveIdx(-1)
		setPlayerNameConfirmed(true)
		setCurrentStep(2)
	}


// --- AUTOCOMPLETE: clavier (↑ ↓ Enter Esc)
	const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (!showSuggestions || suggestions.length === 0) {
			if (e.key === 'Enter') confirmPlayerName()
			return
		}
		if (e.key === 'ArrowDown') {
			e.preventDefault()
			setActiveIdx(i => Math.min(i + 1, suggestions.length - 1))
		} else if (e.key === 'ArrowUp') {
			e.preventDefault()
			setActiveIdx(i => Math.max(i - 1, 0))
		} else if (e.key === 'Enter') {
			e.preventDefault()
			if (activeIdx >= 0) selectGuest(suggestions[activeIdx])
			else confirmPlayerName()
		} else if (e.key === 'Escape') {
			setShowSuggestions(false)
			setActiveIdx(-1)
		}
	}

	useEffect(() => {
		const onClick = (e: MouseEvent) => {
			const t = e.target as Node
			if (
				!nameInputRef.current?.contains(t) &&
				!suggestionsRef.current?.contains(t)
			) {
				setShowSuggestions(false)
				setActiveIdx(-1)
			}
		}
		document.addEventListener('mousedown', onClick)
		return () => document.removeEventListener('mousedown', onClick)
	}, [])

	// Étape 1 : Pâtes/Patate
	const handleTeamPastaChoice = (choice: PastaChoice) => {
		setTeamPasta(choice)
		setCurrentStep(3)
	}

	// Étape 2 : Feta/Mozzarella
	const handleTeamCheeseChoice = (choice: CheeseChoice) => {
		setTeamCheese(choice)
		setCurrentStep(4)
	}

	const determineTeam = (pasta: PastaChoice | null, cheese: CheeseChoice | null, water: WaterChoice | null): PlayerTeam => {
		let juliaPoints = 0;
		let benPoints = 0;

		// ✅ Logique corrigée
		if (pasta === 'pates') benPoints++;      // Pâtes = Benjamin
		if (pasta === 'patate') juliaPoints++;  // Patate = Julia

		if (cheese === 'feta') benPoints++;         // Feta = Benjamin
		if (cheese === 'mozzarella') juliaPoints++; // Mozzarella = Julia

		if (water === 'cristaline') benPoints++;    // Cristaline = Benjamin
		if (water === 'montroucou') juliaPoints++;  // Montroucou = Julia

		return juliaPoints >= benPoints ? 'julia' : 'benjamin';
	}

	// Étape 3 : Cristaline/Montroucou, puis calcul de team dominante
	const handleTeamWaterChoice = (choice: WaterChoice) => {
		setTeamWater(choice)

		// ✅ Déterminer l'équipe finale et passer à l'étape 5
		const finalTeam = determineTeam(teamPasta, teamCheese, choice)
		setPlayerTeam(finalTeam)

		// ✅ Passer directement à l'étape 5 (premier jeu)
		setCurrentStep(5)
	}

	// Saisie prénom
	const confirmPlayerName = () => {
		if (playerName.trim().length >= 2) {
			setPlayerNameConfirmed(true)
			setCurrentStep(2)
		}
	}

	// Quiz duel des mariés
	const handleAnswer = (selectedAuthor: PlayerTeam) => {
		const currentQuestion = duelQuotes[currentQuestionIndex]
		const isCorrect = selectedAuthor === currentQuestion.author

		const result: GameResult = {
			questionId: currentQuestion.id,
			userAnswer: selectedAuthor,
			correctAnswer: currentQuestion.author,
			isCorrect,
		}

		setGameResults(prev => [...prev, result])
		setShowResult(true)

		// Calculer le nouveau score immédiatement
		const newQuizScore = isCorrect ? score + 1 : score
		setScore(newQuizScore)

		setTimeout(() => {
			if (currentQuestionIndex < duelQuotes.length - 1) {
				setCurrentQuestionIndex(prev => prev + 1)
				setShowResult(false)
			} else {
				// C'est la dernière question, marquer le jeu comme terminé
				setGameCompleted(true)
				// La sauvegarde se fera maintenant uniquement quand on passe au Love Run Game
			}
		}, 1000)
	}
	const resetAll = () => {
		setCurrentStep(1)
		setPlayerName('')
		setPlayerNameConfirmed(false)
		setTeamPasta(null)
		setTeamCheese(null)
		setTeamWater(null)
		setPlayerTeam(null)
		setCurrentQuestionIndex(0)
		setGameResults([])
		setShowResult(false)
		setGameCompleted(false)
		setResultSent(false)
		setScore(0)
		setMiniGameScore(null)
	}

	useEffect(() => {
		const checkPreviousAttempt = async () => {
			if (playerNameConfirmed && !playerTeam) {
				const userId = selectedGuestId ? `guest_${selectedGuestId}` : generateUserId(undefined, playerName);

				try {
					const response = await apiService.checkUserAttempts(userId);
					if (response.success && response.data && response.data.totalAttempts > 0) {
						const scoresResponse = await apiService.getBestScores(userId);
						if (scoresResponse.success && scoresResponse.data && scoresResponse.data.length > 0) {
							console.log('Scores précédents:', scoresResponse.data);

							// ✅ Récupérer l'équipe depuis la base de données
							const firstScore = scoresResponse.data[0];
							if (firstScore && firstScore.team) {
								console.log('Équipe récupérée de la DB:', firstScore.team);
								setPlayerTeam(firstScore.team);
								setCurrentStep(5);
							} else {
								console.log('Pas d\'équipe trouvée, recommencer le processus');
								setCurrentStep(2);
							}
						} else {
							setCurrentStep(2);
						}
					} else {
						setCurrentStep(2);
					}
				} catch (error) {
					console.error('Erreur lors de la vérification des tentatives précédentes:', error);
					setCurrentStep(2);
				}
			}
		};

		checkPreviousAttempt();
	}, [playerNameConfirmed, playerTeam, selectedGuestId, playerName]);

	const generateUserId = (guestId?: number, name?: string) => {
		if (guestId) {
			return `guest_${guestId}`
		}
		return `user_${name?.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}`
	}

	return (
		<section id="en-attendant-le-jour-j"
				 className="flex flex-col py-10 section-bg-gradient relative overflow-hidden min-h-[50em]">
			<FloatingEmoji className="opacity-20" />

			{/* Header */}
			<motion.div
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8 }}
				viewport={{ once: true }}
				className="text-center"
			>
				<h2 className="heading-secondary mt-24 mb-4">
					Toi aussi tu trouves le temps long jusqu'au jour J ?
				</h2>
				<div className="w-24 h-1 bg-bordeaux-600 mx-auto mb-12"></div>
			</motion.div>

			{/* Body */}
			<motion.div
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8, delay: 0.2 }}
				viewport={{ once: true }}
				className="max-w-2xl mx-auto"
			>
				{playerNameConfirmed && playerTeam && currentStep >= 5 ? (
					<AttemptManager
						userId={selectedGuestId ? `guest_${selectedGuestId}` : generateUserId(undefined, playerName)}
						playerName={playerName}
						team={playerTeam}
					>
						{(canPlay, submitScores) => (
							<>
								{/* Étape 5 : mini-jeu, dépend de la team */}
								{currentStep === 5 && canPlay && (
									<div className="space-y-8">
										{playerTeam === 'benjamin' ? (
											<JeuBen onComplete={(benScore: number) => {
												setMiniGameScore(benScore)
												setCurrentStep(6)
											}} />
										) : (
											<JeuJulia onComplete={(juliaScore: number) => {
												setMiniGameScore(juliaScore)
												setCurrentStep(6)
											}} />
										)}
									</div>
								)}

								{/* Étape 6 : Quiz Duel des Mariés */}
								{currentStep === 6 && canPlay && (
									<div className="space-y-8">
										{!gameCompleted && (
											<div className="card border-purple-200">
												<div className="mb-6">
													<div className="flex justify-between items-center mb-2">
										<span className="text-sm font-medium text-bordeaux-700">
											Question {currentQuestionIndex + 1} / {duelQuotes.length}
										</span>
														<span className="text-sm text-bordeaux-600">
											Score: {score}/{currentQuestionIndex}
										</span>
													</div>
													<div className="w-full bg-bordeaux-100 rounded-full h-2">
														<div
															className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all duration-300"
															style={{ width: `${((currentQuestionIndex + 1) / duelQuotes.length) * 100}%` }}
														/>
													</div>
												</div>
												<div className="text-center mb-8">
													<Target className="w-12 h-12 text-purple-600 mx-auto mb-4" />
													<h3 className="text-2xl text-bordeaux-700 mb-2">
														Qui a dit ça ?
													</h3>
													<p className="text-bordeaux-600">
														Salut {playerName} ! Devine qui de Julia ou Benjamin a dit cette phrase
													</p>
												</div>
												{/* Citation */}
												<div className="bg-white bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6 mb-8 border-l-4 border-purple-500 shadow-sm">
													<div className="text-center">
														<MagicStarIcon className="w-8 h-8 text-purple-500 mx-auto mb-4" />
														<p className="text-lg text-bordeaux-700 font-medium italic leading-relaxed">
															"{duelQuotes[currentQuestionIndex]?.text}"
														</p>
													</div>
												</div>
												{/* Boutons de choix */}
												{!showResult && (
													<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
														<motion.button
															onClick={() => handleAnswer('julia')}
															whileHover={{ scale: 1.02 }}
															whileTap={{ scale: 0.98 }}
															className="p-6 bg-gradient-to-br from-rose-100 to-pink-100 border-2 border-rose-200 rounded-lg hover:border-rose-300 transition-all duration-200 group relative overflow-hidden"
														>
															<div className="text-center relative z-10">
																<BrideIcon className="w-12 h-12 text-rose-600 mx-auto mb-3 group-hover:scale-110 transition-transform duration-200" />
																<h4 className="text-xl font-semibold text-bordeaux-700 mb-2">Julia</h4>
																<p className="text-sm text-bordeaux-600">C'est Julia qui a dit ça !</p>
															</div>
														</motion.button>
														<motion.button
															onClick={() => handleAnswer('benjamin')}
															whileHover={{ scale: 1.02 }}
															whileTap={{ scale: 0.98 }}
															className="p-6 bg-gradient-to-br from-blue-100 to-indigo-100 border-2 border-blue-200 rounded-lg hover:border-blue-300 transition-all duration-200 group relative overflow-hidden"
														>
															<div className="text-center relative z-10">
																<GroomIcon className="w-12 h-12 text-blue-600 mx-auto mb-3 group-hover:scale-110 transition-transform duration-200" />
																<h4 className="text-xl font-semibold text-bordeaux-700 mb-2">Benjamin</h4>
																<p className="text-sm text-bordeaux-600">C'est Benjamin qui a dit ça !</p>
															</div>
														</motion.button>
													</div>
												)}
												{/* Résultat question */}
												{showResult && (
													<AnimatePresence>
														<motion.div
															initial={{ opacity: 0, scale: 0.8 }}
															animate={{ opacity: 1, scale: 1 }}
															exit={{ opacity: 0, scale: 0.8 }}
															className={`text-center p-6 rounded-lg ${
																gameResults[gameResults.length - 1]?.isCorrect
																	? 'bg-green-100 border-green-200'
																	: 'bg-red-100 border-red-200'
															} border-2`}
														>
															<div className="text-6xl mb-4">
																{gameResults[gameResults.length - 1]?.isCorrect ? '🎉' : '😅'}
															</div>
															<h4 className="text-xl font-semibold text-bordeaux-700 mb-2">
																{gameResults[gameResults.length - 1]?.isCorrect
																	? 'Bravo !'
																	: `C'était ${duelQuotes[currentQuestionIndex]?.author === 'julia' ? 'Julia' : 'Benjamin'} !`
																}
															</h4>
															<p className="text-bordeaux-600">
																{currentQuestionIndex < duelQuotes.length - 1
																	? 'Question suivante dans 2 secondes...'
																	: 'Jeu terminé ! Passons au jeu rétro...'}
															</p>
														</motion.div>
													</AnimatePresence>
												)}
											</div>
										)}
										{/* Résumé final */}
										{gameCompleted && (
											<div className="card bg-gradient-to-br from-yellow-100 to-amber-200 border-4 border-yellow-300 shadow-2xl rounded-3xl p-10 max-w-xl mx-auto animate-fade-in">
												<div className="text-center mb-8">
													<h3 className="text-3xl text-bordeaux-800 mb-3 tracking-tight drop-shadow-sm">
														Duel des Mariés terminé !
													</h3>
													<p className="text-2xl text-bordeaux-700 font-bold mb-2">
														Score final : <span className="text-yellow-600">{score}</span> / {duelQuotes.length}
													</p>
													<p className="text-bordeaux-600 text-base italic mb-2">Bravo pour ta participation 💖</p>
												</div>
												<div className="flex flex-col items-center space-y-6">
													{/* ✅ Bouton modifié - NE PAS soumettre les scores ici */}
													<motion.button
														onClick={() => {
															// Juste passer à l'étape suivante, sans soumettre les scores
															setCurrentStep(7)
														}}
														whileHover={{ scale: 1.05 }}
														whileTap={{ scale: 0.97 }}
														className="w-full p-6 bg-gradient-to-br from-purple-200 to-indigo-200 border-2 border-purple-300 rounded-xl hover:border-purple-400 shadow-md transition-all duration-200 group relative"
													>
														<div className="flex flex-col items-center">
															<div className="flex items-center justify-center gap-6 mb-2">
																<PixelCharacter character="julia" size={48} />
																<PixelCharacter character="ben" size={48} />
															</div>
															<h4 className="text-xl font-semibold text-bordeaux-800 mb-1">Une dernière chance !</h4>
															<p className="text-base text-bordeaux-600">
																Aide Julia & Benjamin à atteindre le mariage !
															</p>
														</div>
													</motion.button>
													{/*<div className="w-full text-center pt-4 border-t border-amber-300">*/}
													{/*	<button*/}
													{/*		onClick={resetAll}*/}
													{/*		className="text-base text-bordeaux-500 hover:text-bordeaux-700 flex items-center justify-center mx-auto gap-2 transition-colors"*/}
													{/*	>*/}
													{/*		<RefreshCw className="w-5 h-5 mr-1" />*/}
													{/*		Recommencer le parcours*/}
													{/*	</button>*/}
													{/*</div>*/}
												</div>
											</div>
										)}
									</div>
								)}

								{/* Étape 7 : jeu rétro */}
								{currentStep === 7 && canPlay && (
									<LoveRunGame
										onBack={resetAll}
										onGameEnd={async (loveRunScore: number) => {
											const allScores = prepareGameScores()
											allScores["loverungame"] = { score: loveRunScore, maxScore: 500 }
											await submitScores(allScores)
										}}
									/>
								)}

								{/* ✅ Message si plus de tentatives */}
								{!canPlay && (
									<div className="card bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
										<div className="text-center p-8">
											<div className="text-6xl mb-4">🎮</div>
											<h3 className="text-2xl text-bordeaux-700 mb-4">Mode Entraînement</h3>
											<p className="text-bordeaux-600 mb-6">
												Vos tentatives officielles sont terminées, mais vous pouvez continuer
												à jouer en mode entraînement !
											</p>
											<p className="text-sm text-bordeaux-500 mb-6">
												⚠️ Les scores en mode entraînement ne seront pas sauvegardés
											</p>
											<div className="flex gap-4 justify-center">
												<button
													onClick={resetAll}
													className="btn-secondary"
												>
													Retour au début
												</button>
											</div>
										</div>
									</div>
								)}
							</>
						)}
					</AttemptManager>
				) : (
					<>
						{/* Étape 1 : prénom + AUTOCOMPLETE */}
						{currentStep === 1 && (
							<div className="card bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
								<div className="text-center mb-8">
									<h3 className="text-2xl text-bordeaux-700 mb-8">
										Viens, on va te faire patienter !
									</h3>
								</div>

								<div className="max-w-md mx-auto">
									<div className="relative">
										<input
											type="text"
											ref={nameInputRef}
											value={playerName}
											onChange={handlePlayerInputChange}
											onFocus={() => setShowSuggestions(true)}
											onKeyDown={handleNameKeyDown}
											className="w-full pl-4 pr-4 py-4 border-2 border-bordeaux-200 rounded-2xl focus:outline-none focus:ring-4 focus:ring-bordeaux-200 focus:border-bordeaux-500 transition-all duration-200 text-lg text-center bg-white/80 backdrop-blur-sm"
											placeholder="Ton prénom ..."
											autoComplete="off"
										/>
										{isLoadingName && (
											<div className="absolute left-3 top-1/2 -translate-y-1/2">
												<Loader2 className="w-5 h-5 text-bordeaux-400 animate-spin" />
											</div>
										)}

										{/* Dropdown suggestions (style RSVP) */}
										<AnimatePresence>
											{showSuggestions && (
												<motion.div
													ref={suggestionsRef}
													initial={{ opacity: 0, y: -10, scale: 0.98 }}
													animate={{ opacity: 1, y: 0, scale: 1 }}
													exit={{ opacity: 0, y: -10, scale: 0.98 }}
													transition={{ duration: 0.15 }}
													className="absolute z-50 left-0 right-0 top-full mt-2 bg-white border-2 border-bordeaux-200 rounded-2xl max-h-80 overflow-y-auto shadow-2xl"
												>
													{!isLoadingName && playerName.trim().length < 2 && (
														<div className="px-6 py-4 text-bordeaux-400 text-center">
															Veuillez entrer au moins 2 lettres
														</div>
													)}

													{!isLoadingName &&
														playerName.trim().length >= 2 &&
														suggestions.length === 0 && (
															<div className="px-6 py-4 text-bordeaux-400 text-center flex items-center justify-center">
																<AlertCircle className="w-4 h-4 mr-2" />
																Aucun invité trouvé
															</div>
														)}

													{suggestions.map((g, idx) => (
														<div
															key={g.id}
															className={`px-6 py-4 cursor-pointer transition-colors border-b border-bordeaux-100 last:border-b-0 flex items-center justify-between ${
																idx === activeIdx ? 'bg-bordeaux-50' : 'hover:bg-bordeaux-50'
															}`}
															onMouseEnter={() => setActiveIdx(idx)}
															onMouseLeave={() => setActiveIdx(-1)}
															onClick={() => selectGuest(g)}
														>
															<div className="flex items-center">
																<div className="w-10 h-10 bg-bordeaux-100 rounded-full flex items-center justify-center mr-3">
																	<User className="w-5 h-5 text-bordeaux-600" />
																</div>
																<div className="font-semibold text-bordeaux-700">
																	{g.firstName} {g.lastName}
																</div>
															</div>
														</div>
													))}
												</motion.div>
											)}
										</AnimatePresence>
									</div>

									{/* Bouton continuer (fallback si pas de sélection) */}
									<button
										onClick={confirmPlayerName}
										disabled={playerName.trim().length < 2}
										className="btn-primary w-full mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
									>
										Continuer
									</button>
								</div>
							</div>
						)}

						{/* Étape 2 : pâtes/patate */}
						{currentStep === 2 && (
							<div className="card bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200">
								<div className="text-center mb-12">
									<h3 className="text-4xl text-bordeaux-700 mb-2">
										Choisis ton camp !
									</h3>
									<p className="text-bordeaux-600">Team pâtes ou Team patate ?</p>
								</div>
								<div className="flex flex-col md:flex-row gap-6 justify-center">
									<motion.button
										onClick={() => handleTeamPastaChoice('pates')}
										whileHover={{ scale: 1.05 }}
										whileTap={{ scale: 0.98 }}
										className={`flex-1 p-8 bg-gradient-to-br from-orange-50 to-orange-200 border-none rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-200 group ${teamPasta === 'pates' ? 'ring-4 ring-orange-400' : ''}`}
									>
										<div className="flex flex-col items-center justify-center h-52 w-52 mx-auto bg-white/80 rounded-2xl shadow-lg border border-orange-100">
											<Image src="/img/pasta.png" alt="Pâtes" width={140} height={140} className="object-contain drop-shadow-md" />
											<span className="mt-4 text-lg font-semibold text-orange-700">Pâtes</span>
										</div>
									</motion.button>
									<motion.button
										onClick={() => handleTeamPastaChoice('patate')}
										whileHover={{ scale: 1.05 }}
										whileTap={{ scale: 0.98 }}
										className={`flex-1 p-8 bg-gradient-to-br from-yellow-50 to-amber-200 border-none rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-200 group ${teamPasta === 'patate' ? 'ring-4 ring-yellow-400' : ''}`}
									>
										<div className="flex flex-col items-center justify-center h-52 w-52 mx-auto bg-white/80 rounded-2xl shadow-lg border border-yellow-100">
											<Image src="/img/potato.png" alt="Patate" width={140} height={140} className="object-contain drop-shadow-md" />
											<span className="mt-4 text-lg font-semibold text-yellow-700">Patate</span>
										</div>
									</motion.button>
								</div>
							</div>
						)}

						{/* Étape 3 : feta/mozzarella */}
						{currentStep === 3 && (
							<div className="card bg-gradient-to-br from-green-50 to-amber-100 border-green-200 shadow-lg rounded-xl p-8">
								<div className="text-center mb-6">
									<h3 className="text-3xl text-bordeaux-700 mb-2">Team Feta ou Team Mozzarella ?</h3>
								</div>
								<div className="flex flex-col md:flex-row gap-6 justify-center">
									<motion.button
										onClick={() => handleTeamCheeseChoice('feta')}
										whileHover={{ scale: 1.05 }}
										whileTap={{ scale: 0.98 }}
										className={`flex-1 p-8 bg-gradient-to-br from-green-50 to-green-200 border-none rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-200 group ${teamCheese === 'feta' ? 'ring-4 ring-green-400' : ''}`}
									>
										<div className="flex flex-col items-center justify-center h-52 w-52 mx-auto bg-white/80 rounded-2xl shadow-lg border border-green-100">
											<Image src="/img/feta.png" alt="Feta" width={140} height={140} className="object-contain drop-shadow-md" />
											<span className="mt-4 text-lg font-semibold text-green-700">Feta</span>
										</div>
									</motion.button>
									<motion.button
										onClick={() => handleTeamCheeseChoice('mozzarella')}
										whileHover={{ scale: 1.05 }}
										whileTap={{ scale: 0.98 }}
										className={`flex-1 p-8 bg-gradient-to-br from-yellow-50 to-yellow-200 border-none rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-200 group ${teamCheese === 'mozzarella' ? 'ring-4 ring-yellow-400' : ''}`}
									>
										<div className="flex flex-col items-center justify-center h-52 w-52 mx-auto bg-white/80 rounded-2xl shadow-lg border border-yellow-100">
											<Image src="/img/mozza.png" alt="Mozzarella" width={140} height={140} className="object-contain drop-shadow-md" />
											<span className="mt-4 text-lg font-semibold text-yellow-700">Mozzarella</span>
										</div>
									</motion.button>
								</div>
							</div>
						)}

						{/* Étape 4 : cristalline/montroucou */}
						{currentStep === 4 && (
							<div className="card bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200">
								<div className="text-center mb-6">
									<Users className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
									<h3 className="text-2xl text-bordeaux-700 mb-2">
										Team Cristaline ou Team Montroucou ?
									</h3>
									<p className="text-bordeaux-600">Choisis ton camp !</p>
								</div>
								<div className="flex flex-col md:flex-row gap-6 justify-center">
									<motion.button
										onClick={() => handleTeamWaterChoice('cristaline')}
										whileHover={{ scale: 1.05 }}
										whileTap={{ scale: 0.98 }}
										className={`flex-1 p-8 bg-gradient-to-br from-cyan-50 to-cyan-200 border-none rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-200 group ${teamWater === 'cristaline' ? 'ring-4 ring-cyan-400' : ''}`}
									>
										<div className="flex flex-col items-center justify-center h-52 w-52 mx-auto bg-white/80 rounded-2xl shadow-lg border border-cyan-100">
											<Image src="/img/cristaline.png" alt="Cristaline" width={140} height={140} className="object-contain drop-shadow-md" />
											<span className="mt-4 text-lg font-semibold text-cyan-700">Cristaline</span>
										</div>
									</motion.button>
									<motion.button
										onClick={() => handleTeamWaterChoice('montroucou')}
										whileHover={{ scale: 1.05 }}
										whileTap={{ scale: 0.98 }}
										className={`flex-1 p-8 bg-gradient-to-br from-yellow-50 to-amber-200 border-none rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-200 group ${teamWater === 'montroucou' ? 'ring-4 ring-yellow-400' : ''}`}
									>
										<div className="flex flex-col items-center justify-center h-52 w-52 mx-auto bg-white/80 rounded-2xl shadow-lg border border-yellow-100">
											<Image src="/img/montroucous.png" alt="Montroucou" width={140} height={140} className="object-contain drop-shadow-md" />
											<span className="mt-4 text-lg font-semibold text-yellow-700">Montroucou</span>
										</div>
									</motion.button>
								</div>
							</div>
						)}
					</>
				)}
			</motion.div>
		</section>
	)
}

// @ts-ignore
export default EnAttendantLeJourJ
